import { FC, useEffect, useMemo, useRef } from 'react'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'
import { SelectPicker } from 'rsuite'
import { useConfigure, useSortBy } from 'react-instantsearch'

import { CatalogType, isDefined } from '../../../utils/types/misc'
import { SortOptions } from '../../../utils/types/Product'
import useFiltersClose from '../../hooks/useFiltersClose'
import { QueryParam, SortOption, TypesenseSortOption } from '../../../utils/constants'
import useSortStateWithQuery from '../../../services/useSortStateWithQuery'

import styles from '../../../styles/CatalogFilters.module.less'

type SortArrayKey = TypesenseSortOption
type SortByItemArray = Parameters<typeof useSortBy>[0]['items'] & { value: SortArrayKey }[]

interface SortPickerProps {
  onSortSelect: (value: SortOptions | null) => void
  catalogType: CatalogType
  shouldCloseFilters?: boolean
}

const SortPicker: FC<SortPickerProps> = (props) => {
  const { onSortSelect, catalogType, shouldCloseFilters } = props
  const { query } = useRouter()
  const sortQuery = query[QueryParam.sort]
  const [sortOption] = useSortStateWithQuery()

  const { t } = useTranslation('products')
  const sortPickerRef = useRef<HTMLDivElement>(null)
  const isOpen = useFiltersClose(shouldCloseFilters)

  const queryValueMap: Record<SortArrayKey, SortOption> = {
    products: SortOption.popular,
    'products/sort/ctime:desc': SortOption.newest,
    'products/sort/_eval(referencePrice:0):desc,pos:asc': SortOption.quote_only,
    'products/sort/_eval(referencePrice:0):asc,referencePrice:asc': SortOption.lowest,
    'products/sort/referencePrice:desc': SortOption.highest,
    'products/sort/supplier:asc': SortOption.name_az,
    'products/sort/supplier:desc': SortOption.name_za,
  }

  const valueQueryMap: Record<SortOption, SortArrayKey> = {
    popular: 'products',
    newest: 'products/sort/ctime:desc',
    'quote-only': 'products/sort/_eval(referencePrice:0):desc,pos:asc',
    lowest: 'products/sort/_eval(referencePrice:0):asc,referencePrice:asc',
    highest: 'products/sort/referencePrice:desc',
    'name-az': 'products/sort/supplier:asc',
    'name-za': 'products/sort/supplier:desc',
  }

  const commonSortOptions: SortByItemArray = [
    {
      label: t('Popular'),
      value: 'products',
    },
    {
      label: t('Newest'),
      value: 'products/sort/ctime:desc',
    },
    {
      label: t('Quote only'),
      value: 'products/sort/_eval(referencePrice:0):desc,pos:asc',
    },
  ]

  const sortOptions: Record<CatalogType, SortByItemArray> = {
    products: [
      ...commonSortOptions,
      {
        label: t('Lowest price'),
        value: 'products/sort/_eval(referencePrice:0):asc,referencePrice:asc',
      },
      {
        label: t('Highest price'),
        value: 'products/sort/referencePrice:desc',
      },
    ],
    suppliers: [
      ...commonSortOptions,
      {
        label: t('Name (A-Z)'),
        value: 'products/sort/supplier:asc',
      },
      {
        label: t('Name (Z-A)'),
        value: 'products/sort/supplier:desc',
      },
    ],
  }

  const currentCatalogSortOptions = useMemo(() => sortOptions[catalogType], [catalogType, t])

  // To keep the selected option at top
  const modifiedSortOptions = [
    currentCatalogSortOptions.find((item) => sortOption === item.value),
    ...currentCatalogSortOptions.filter((item) => sortOption !== item.value),
  ].filter(isDefined)

  const {
    currentRefinement,
    refine,
  } = useSortBy({
    items: currentCatalogSortOptions,
  })

  useEffect(() => {
    if (!sortQuery) {
      refine('products')
      return
    }
    if (sortQuery && typeof sortQuery === 'string' && sortQuery !== queryValueMap[currentRefinement as SortArrayKey]) {
      Object
        .entries(queryValueMap)
        .filter(([, value]) => value === sortQuery)
        .forEach(([key]) => refine(key))
    }
  }, [sortQuery, currentRefinement])

  const getSelectPickerClass = () => ((!sortOption || sortOption === SortOption.popular) ? styles['select-picker'] : styles['selected-value-picker'])

  return (
    <div
      ref={sortPickerRef}
      className="relative"
      data-testid="sort-picker-container"
    >
      <SelectPicker
        data={modifiedSortOptions}
        label={t('common:Sort')}
        onChange={(value) => {
          onSortSelect(value ? queryValueMap[value as TypesenseSortOption] : null)
        }}
        value={valueQueryMap[sortOption as SortOption] || 'products'}
        cleanable={false}
        searchable={false}
        container={sortPickerRef.current || undefined}
        block
        open={isOpen}
        className={getSelectPickerClass()}
        aria-label="sort-picker"
        data-key="sort-picker"
        data-testid="sort-picker"
      />
    </div>
  )
}

export default SortPicker
